<template>
  <TemplateBase :page="page">
    <div class="hidden"><!-- This health check is used by Exonet.  Do NOT remove it from the homepage! -->app-health-check:home</div>

    <!--  START HOME GRID (necessary to hold the header, the newsletter image, and the discovery gradient
          BEWARE!! these images are in separate rows, so in different layers underneath the content !!!) -->
    <div class="grid grid-cols-12">
      <div v-if="page.image || page.video" class="c-home-mask relative z-0 row-start-1 row-end-8 col-span-full flex items-start justify-start relative z-10">
        <MoleculeMaskVisual class="sm:hidden" :image="page.video ? undefined : page.image" :has-video="!!page.video" :shape-number="4" label="" role="presentation">
          <video-background v-if="page.video" :src="page.video.url" class="c-home-video-player w-full" :muted="true" />
        </MoleculeMaskVisual>
        <MoleculeMaskVisual class="hidden sm:block" :image="page.video ? undefined : page.image" :has-video="!!page.video" :shape-number="2" label="" role="presentation">
          <video-background v-if="page.video" :src="page.video.url" class="c-home-video-player w-full" :muted="true" />
        </MoleculeMaskVisual>
      </div>
      <OrganismHomeHeader
        class="c-home-header row-start-1 col-span-full relative z-20"
        :title="page.title"
        :subtitle="page.subtitle"
        :buttons="page.homeButtons"
        :sponsors="page.sponsors"
      />

      <MoleculeABTestWrapper experiment-code="cv13" default-variant="A" class="contents">
        <template #A>
          <OrganismHomeSearchConcertsControl
            class="c-home-search-concerts row-start-2 col-span-full relative z-20"
            :recommendation-url="page.recommendation && page.recommendation.url"
            :genres="page.genres"
          />
        </template>
        <template #B>
          <OrganismHomeSearchConcertsV1
            class="c-home-search-concerts row-start-2 col-span-full relative z-20"
            :recommendation-url="page.recommendation && page.recommendation.url"
            :genres="page.genres"
          />
        </template>
      </MoleculeABTestWrapper>
      <OrganismHomeEvents class="row-start-3 col-span-full relative z-10 mb-16" :events="page.events" :button-url="eventOverviewUrl" />
      <OrganismHomeBanners class="row-start-4 col-span-full relative z-10 mb-8 sm:mb-12" :banners-small="page.bannersSmall" :banners-large="page.bannersLarge" />
      <!--  Seperate section with discovery background svg and gradient that reaches from row 1 to row 6 with a z-index of 0
            This way it can sit in the background of the page and line neatly with the discover section!
      -->
      <section
        v-if="page.language === 'nl' && page.discoveries.length && page.discoveryCollections.length"
        class="relative z-0 pointer-events-none row-start-1 row-end-6 col-span-full v-gradient-bg v-gradient-bg--gold v-gradient-bg--fade c-home-discover-end"
      />
      <OrganismHomeDiscovery
        v-if="page.language === 'nl'"
        class="row-start-5 col-span-full relative z-10 c-home-discover-end"
        :discoveries="page.discoveries"
        :discovery-collections="page.discoveryCollections"
      />
      <OrganismHomeOverview class="row-start-6 col-span-full relative z-10 mb-8 sm:mb-0 lg:mb-12" :overview-items="groupedOverviewDynamicContent" />
      <OrganismHomeNewsletter
        class="row-start-7 col-span-full relative z-10 mb-32 sm:mb-8 md:mb-4 lg:mb-16 xl:mb-32"
        :url="page.newsletter.url"
        :label="page.newsletter.label"
        :title="page.newsletter.newsletterTitle"
        :origin="page.newsletter.origin"
      />

      <!-- separate newsletter image -->
      <div v-if="page.newsletter.url && page.newsletter.title && page.newsletter.image" class="relative z-0 row-start-1 row-end-8 col-span-full flex items-end justify-end">
        <MoleculeMaskVisual class="sm:hidden" :image="page.newsletter.image" :shape-number="5" />
        <MoleculeMaskVisual class="hidden sm:block" :image="page.newsletter.image" :shape-number="3" />
      </div>
    </div>
    <!-- END HOME GRID-->
  </TemplateBase>
</template>

<script setup lang="ts">
const props = defineProps({
  page: { type: Object as PropType<Record<string, any>>, required: true },
})

const globalStore = useGlobalStore()
const { groupedItems: groupedOverviewDynamicContent } = setupDynamicContentItems(props.page.overviewDynamicContent)
const eventOverviewUrl = globalStore.eventOverview.url
</script>

<style lang="scss" class="scoped">
// Fixed mask heights per breakpoint so the boog has a predictable position,
// independent of viewport height. The search blocks and "Binnenkort" margin
// below are tuned to land just after the mask.
.c-home-mask .mask-visual--2,
.c-home-mask .mask-visual--4 {
  height: 580px;

  @screen md {
    height: 500px;
  }

  @screen lg {
    height: 620px;
  }

  @screen xl {
    height: 740px;
  }

  @screen 2xl {
    height: 780px;
  }
}

.c-home-search-concerts {
  @apply mb-6;

  @screen sm {
    @apply mb-12;
  }

  @screen md {
    @apply mb-16;
  }

  @screen lg {
    @apply mb-24;
  }

  @screen xl {
    @apply mb-32;
  }

  @screen 2xl {
    @apply mb-40;
  }
}

// Fills the mask above, which already sets the height per breakpoint. Do not give this its own height:
// the video covers its container, so anything taller than the mask is a crop the mask then clips.
.c-home-video-player {
  height: 100%;
}

.c-home-header {
  padding-bottom: 5.5vw;

  @screen sm {
    @apply pb-8;
  }

  @screen lg {
    @apply pb-8;
  }
}

.c-home-discover-end {
  // used on the 2 discovery sections to achieve the correct layout with the golden gradient
  @apply pb-10 mb-10;

  @screen md {
    @apply pb-14 mb-12;
  }

  @screen lg {
    @apply pb-16 mb-16;
  }
}

.row-end-8 {
  grid-row-end: 8;
}
</style>
